import { todayWithSystemTimeZone } from '@magicdoorinc/date';
import { ValidationError } from '~/errors';
import { validateId } from '~/repositories/utils';
import { fetchInChunks } from '~/utils/batch';
import { urlWithQuery } from '~/utils/url';
import { BaseRestRepository } from './baseRestRepository';
import { uploadSessionRepository } from './uploadSessionRepository';
import type {
  CreatePropertyDto,
  HydratedOwnerPropertyDto,
  HydratedPropertyDto,
  HydratedPropertyPaginationDto,
  PropertyOverviewDto,
  PropertyOwnershipDto,
  PropertyType,
  UpdatePropertyDto,
  UpdateUnitMagicTagDto,
} from '~/swagger/portal/Api';
import type { ApiFilter } from '~/types/apiFilters';

const PROPERTIES_URL = '/api/properties';

export function validateProperties(properties: CreatePropertyDto | UpdatePropertyDto): void {
  if (!properties) throw new ValidationError('Properties object must be provided');
}

export type PropertiesFilter = ApiFilter<{
  portfolioId?: string;
  ownerId?: string;
  type?: `${PropertyType}`;
}>;

export type PropertyImagePayload = {
  file: File;
};

export class PropertiesRepository extends BaseRestRepository {
  public async getProperties(filter?: PropertiesFilter): Promise<HydratedPropertyPaginationDto> {
    const url = urlWithQuery(PROPERTIES_URL, filter);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async getProperty(propertyId: string): Promise<HydratedPropertyDto> {
    validateId(propertyId);
    const url = `${PROPERTIES_URL}/${propertyId}`;
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  // 数量限制100个
  public async batchGetPropertyByIds(propertyIds: string[]): Promise<HydratedPropertyDto[]> {
    return fetchInChunks(propertyIds, async (idsChunk) => {
      const url = urlWithQuery(`${PROPERTIES_URL}/batch`, { propertyIds: idsChunk });
      const response = await this.fetchWithAuth(url);
      return this.getJsonResponse(response);
    });
  }

  public async createProperty(property: CreatePropertyDto): Promise<HydratedPropertyDto> {
    validateProperties(property);
    const url = PROPERTIES_URL;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(property),
    });
    return this.getJsonResponse(response);
  }

  public async updateProperty(propertyId: string, property: UpdatePropertyDto): Promise<HydratedPropertyDto> {
    validateId(propertyId);
    validateProperties(property);
    const url = `${PROPERTIES_URL}/${propertyId}`;
    const response = await this.fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify(property),
    });
    return this.getJsonResponse(response);
  }

  /** Not preferred. use deactivateProperty instead in most cases */
  public async deleteProperty(propertyId: string): Promise<void> {
    validateId(propertyId);
    const url = `${PROPERTIES_URL}/${propertyId}`;
    await this.fetchWithAuth(url, { method: 'DELETE' });
  }

  public async deactivateProperty(propertyId: string, forceDeactivateChildren?: boolean): Promise<void> {
    validateId(propertyId);
    const url = urlWithQuery(`${PROPERTIES_URL}/${propertyId}/deactivate`, { forceDeactivateChildren });
    await this.fetchWithAuth(url, { method: 'DELETE' });
  }

  public async removePropertyOwner(propertyId: string, ownerId: string): Promise<void> {
    const url = `${PROPERTIES_URL}/${propertyId}/owners/${ownerId}`;
    await this.fetchWithAuth(url, { method: 'DELETE' });
  }

  public async addPropertyOwner(propertyId: string, ownerId: string, payload: PropertyOwnershipDto): Promise<HydratedOwnerPropertyDto> {
    const url = `${PROPERTIES_URL}/${propertyId}/owners/${ownerId}`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async updatePropertyOwner(propertyId: string, ownerId: string, payload: PropertyOwnershipDto): Promise<HydratedOwnerPropertyDto> {
    const url = `${PROPERTIES_URL}/${propertyId}/owners/${ownerId}`;
    const response = await this.fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async getPropertyOverview(propertyId: string, useUpdatedAt?: boolean): Promise<PropertyOverviewDto> {
    let url = `${PROPERTIES_URL}/${propertyId}/overview`;
    if (useUpdatedAt) {
      url += `?updatedAt=` + todayWithSystemTimeZone().valueOf();
    }
    const response = await this.fetchWithAuth(url, { method: 'GET' });
    return this.getJsonResponse(response);
  }

  public async updatePropertyMagicTags(propertyId: string, magicTags: UpdateUnitMagicTagDto): Promise<HydratedPropertyDto> {
    const url = `${PROPERTIES_URL}/${propertyId}/magic-tags`;
    const response = await this.fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify(magicTags),
    });
    return this.getJsonResponse(response);
  }

  public async moveProperty(propertyId: string, portfolioId: string): Promise<void> {
    const url = `${PROPERTIES_URL}/${propertyId}/move`;
    await this.fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify({ portfolioId }),
    });
  }

  public async uploadPropertyImage(propertyId: string, payload: PropertyImagePayload): Promise<HydratedPropertyDto> {
    validateId(propertyId);
    const [uploadSessionId] = await uploadSessionRepository.uploadFiles(`${PROPERTIES_URL}/${propertyId}/image/upload-sessions`, [
      payload.file,
    ]);

    const url = `${PROPERTIES_URL}/${propertyId}/image`;
    const response = await this.fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify({ uploadSessionId }),
    });
    return this.getJsonResponse(response);
  }
}

export const propertiesRepository = new PropertiesRepository();
